"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Conditional = void 0;
const lodash_1 = require("lodash");
const operators_1 = require("./operators");
class Conditional {
    static configure(options) {
        this.options = Object.assign(Object.assign({}, this.options), options);
    }
    static registerPlugin(plugin) {
        this.plugins.set(plugin.name, plugin);
    }
    static getRegisteredPlugins() {
        return Array.from(this.plugins.keys());
    }
    static isPluginEnabled(pluginName) {
        return (this.options.enablePlugins || []).includes(pluginName);
    }
    static evaluate({ conditions, data, }) {
        if (!conditions) {
            return true;
        }
        if (!Array.isArray(conditions)) {
            conditions = [conditions];
        }
        // Each array element is an OR condition, hence the conditions.some
        // Each object key is an AND condition, hence the Object.entries.every
        return conditions.some((condition) => Object.entries(condition).every(([key, value]) => this.testExpression((0, lodash_1.get)(data, key), value)));
    }
    static evaluateAndReturnEvaluatedData({ conditions, data, }) {
        const matched = this.evaluate({ conditions, data });
        return {
            matched,
            evaluatedData: this._getEvaluatedData(data, conditions),
        };
    }
    static testExpression(data, exp) {
        if (!Array.isArray(exp)) {
            exp = [exp, "$eq"];
        }
        const [value, operator, options] = exp;
        // Try core operators first
        if (this.isCoreOperator(operator)) {
            return this.testCoreExpression(data, exp);
        }
        // Try enabled plugins
        for (const pluginName of this.options.enablePlugins || []) {
            const plugin = this.plugins.get(pluginName);
            if (plugin && plugin.operators.includes(operator)) {
                const result = plugin.testExpression(data, exp);
                if (result !== null) {
                    return result;
                }
            }
        }
        // Handle unknown operators
        if (this.options.throwOnUnknownOperator) {
            throw new Error(`Unknown operator: ${operator}`);
        }
        return false;
    }
    static isCoreOperator(operator) {
        return [
            "$eq",
            "$ne",
            "$in",
            "$nin",
            "$lt",
            "$lte",
            "$gt",
            "$gte",
            "$between",
            "$exists",
            "$regex",
            "$any",
            "$all",
            "$none",
        ].includes(operator);
    }
    static testCoreExpression(data, exp) {
        const [value, operator, options] = exp;
        // Handle array operations first (special case)
        if (["$any", "$all", "$none"].includes(operator)) {
            return operators_1.arrayOperators[operator](data, value, this);
        }
        // Handle array data for most operators (excluding array operations)
        if (Array.isArray(data)) {
            return data.some((element) => this.testExpression(element, exp));
        }
        // Handle object data (should return false for non-array operations, except $exists)
        if (typeof data === "object" && data !== null && operator !== "$exists") {
            return false;
        }
        // Delegate to specific operator handlers
        // At this point, data should only be Value since arrays and objects were handled above
        const valueData = data;
        if (operators_1.basicOperators.hasOwnProperty(operator)) {
            return operators_1.basicOperators[operator](valueData, value, options);
        }
        if (operators_1.numericOperators.hasOwnProperty(operator)) {
            return operators_1.numericOperators[operator](valueData, value, options);
        }
        return false;
    }
    static _getEvaluatedData(currentData, currentConditions) {
        if (!currentConditions) {
            return {};
        }
        if (!Array.isArray(currentConditions)) {
            currentConditions = [currentConditions];
        }
        const evaluatedData = {};
        currentConditions.forEach((condition) => {
            if (typeof condition !== "object") {
                // condition is not an object.. this could happen if the condition is incorrectly defined.
                // for instance, { roles: [["admin"], "$any"] }
                return;
            }
            Object.entries(condition).forEach(([key, value]) => {
                const extractedData = (0, lodash_1.get)(currentData, key);
                if (!Array.isArray(extractedData)) {
                    // special array conditions like $any, $all, $none don't apply
                    // if the data is not an array
                    (0, lodash_1.set)(evaluatedData, key, extractedData);
                    return;
                }
                if (!Array.isArray(value) ||
                    !["$any", "$all", "$none"].includes(value[1])) {
                    // condition is not an array condition
                    (0, lodash_1.set)(evaluatedData, key, extractedData);
                    return;
                }
                // this is an array condition operating on an array..
                // need to recurse
                (0, lodash_1.set)(evaluatedData, key, extractedData.map((innerData, index) => {
                    let innerCondition = value[0];
                    if (!Array.isArray(innerCondition)) {
                        innerCondition = [innerCondition];
                    }
                    return this._getEvaluatedData(innerData, innerCondition);
                }));
            });
        });
        return evaluatedData;
    }
}
exports.Conditional = Conditional;
Conditional.plugins = new Map();
Conditional.options = {
    enablePlugins: [],
    strictMode: false,
    throwOnUnknownOperator: false,
};
//# sourceMappingURL=Conditional.js.map